import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, User2 } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_bf707ab1-d809-4aca-a466-4390571c997b/artifacts/6hmyyhtk_23Recurso%203.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(username.trim(), password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#05080f] text-white">
      {/* subtle blue glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-[60vmin] w-[60vmin] -translate-x-1/2 rounded-full bg-[#1e88ff] opacity-[0.10] blur-[140px]" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-10">
        <img
          src={LOGO_URL}
          alt="Logo"
          className="mb-8 h-24 w-24 object-contain opacity-90 drop-shadow-[0_0_24px_rgba(30,136,255,0.35)]"
          draggable={false}
        />
        <div className="mb-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.6em] text-white/40">
            Portal del cliente
          </div>
          <h1 className="mt-3 font-serif text-3xl text-white/95 sm:text-4xl">
            Playground
          </h1>
          <p className="mt-3 text-sm text-white/50">
            Ingresa para acceder a tus vistas previas
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl"
          data-testid="login-form"
        >
          <div className="space-y-5">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-[0.2em] text-white/50">
                Usuario
              </Label>
              <div className="relative mt-2">
                <User2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="tu_usuario"
                  required
                  autoComplete="username"
                  className="h-11 border-white/10 bg-black/30 pl-9 text-white placeholder:text-white/30"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-white/50">
                Keypass
              </Label>
              <div className="relative mt-2">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="h-11 border-white/10 bg-black/30 pl-9 text-white placeholder:text-white/30"
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="rounded-md border border-red-400/30 bg-red-400/10 px-3 py-2 text-sm text-red-200"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="h-11 w-full rounded-md bg-[#1e88ff] font-medium tracking-wide text-white transition-all hover:bg-[#0f6fe5] disabled:opacity-50"
            >
              {busy ? "Verificando..." : "Acceder"}
            </Button>
          </div>
        </form>

        <p className="mt-8 text-[10px] uppercase tracking-[0.4em] text-white/30">
          Acceso restringido · Contenido protegido
        </p>
      </div>
    </div>
  );
}
